import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Users, Edit2, Trash2, Building } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import type { Department, Team } from '../types';
import Modal from '../components/common/Modal';
import { Input } from '../components/common/Input';
import { Textarea } from '../components/common/Textarea';
import { Select } from '../components/common/Select';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface DepartmentFormData {
  name: string;
  description: string;
  managerId: string;
}

interface TeamFormData {
  name: string;
  description: string;
  departmentId: string;
  managerId: string;
}

const DepartmentManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [selectedDepartment, setSelectedDepartment] = React.useState<Department | null>(null);
  const [selectedTeam, setSelectedTeam] = React.useState<Team | null>(null);
  const [isDeptModalOpen, setIsDeptModalOpen] = React.useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteType, setDeleteType] = React.useState<'department' | 'team'>('department');

  const { register: registerDept, handleSubmit: handleDeptSubmit, reset: resetDept, setValue: setDeptValue, formState: { errors: deptErrors } } = useForm<DepartmentFormData>();
  const { register: registerTeam, handleSubmit: handleTeamSubmit, reset: resetTeam, setValue: setTeamValue, watch: watchTeam, formState: { errors: teamErrors } } = useForm<TeamFormData>();

  const { data: departments, isLoading: deptLoading, isError: deptError, refetch: refetchDepartments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        console.log('Fetching departments...');
        const response = await api.get('/departments');
        console.log('Departments response:', response.data);
        const departments = response.data.departments || response.data.data || response.data || [];
        console.log('Processed departments:', departments.length);
        return departments;
      } catch (error) {
        console.error('Error fetching departments:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  });

  const { data: teams, isLoading: teamsLoading, isError: teamsError, refetch: refetchTeams } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      try {
        console.log('Fetching teams...');
        const response = await api.get('/teams');
        console.log('Teams response:', response.data);
        const teams = response.data.teams || response.data.data || response.data || [];
        console.log('Processed teams:', teams.length);
        return teams;
      } catch (error) {
        console.error('Error fetching teams:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });

  const { data: users, isError: usersError, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        console.log('Fetching users for managers...');
        const response = await api.get('/users');
        console.log('Users response:', response.data);
        const users = response.data.users || response.data.data || response.data || [];
        console.log('Processed users:', users.length);
        return users;
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000
  });

  const createDepartmentMutation = useMutation({
    mutationFn: async (data: DepartmentFormData) => {
      const createPayload = {
        name: data.name,
        description: data.description,
        manager_id: data.managerId ? parseInt(data.managerId) : null
      };
      
      console.log('Creating department with payload:', createPayload);
      const response = await api.post('/departments', createPayload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Dział utworzony pomyślnie');
      setIsDeptModalOpen(false);
      resetDept();
    },
    onError: (error: any) => {
      console.error('Department creation error:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas tworzenia działu');
    }
  });

  const updateDepartmentMutation = useMutation({
    mutationFn: async (data: { id: number; data: DepartmentFormData }) => {
      // Prepare the update payload with proper manager handling
      const updatePayload = {
        name: data.data.name,
        description: data.data.description,
        manager_id: data.data.managerId ? parseInt(data.data.managerId) : null
      };
      
      console.log('Updating department with payload:', updatePayload);
      const response = await api.put(`/departments/${data.id}`, updatePayload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users'] }); // In case manager assignments affect user data
      
      const action = variables.data.managerId ? 'zaktualizowany' : 'zaktualizowany (kierownik usunięty)';
      toast.success(`Dział ${action} pomyślnie`);
      setIsDeptModalOpen(false);
      setSelectedDepartment(null);
      resetDept();
    },
    onError: (error: any) => {
      console.error('Department update error:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji działu');
    }
  });

  const deleteDepartmentMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/departments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Dział usunięty');
      setDeleteModalOpen(false);
      setSelectedDepartment(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania działu');
    }
  });

  const createTeamMutation = useMutation({
    mutationFn: async (data: TeamFormData) => {
      const createPayload = {
        name: data.name,
        description: data.description,
        department_id: parseInt(data.departmentId),
        manager_id: data.managerId ? parseInt(data.managerId) : null
      };
      
      console.log('Creating team with payload:', createPayload);
      const response = await api.post('/teams', createPayload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Zespół utworzony pomyślnie');
      setIsTeamModalOpen(false);
      resetTeam();
    },
    onError: (error: any) => {
      console.error('Team creation error:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas tworzenia zespołu');
    }
  });

  const updateTeamMutation = useMutation({
    mutationFn: async (data: { id: number; data: TeamFormData }) => {
      // Prepare the update payload with proper manager handling
      const updatePayload = {
        name: data.data.name,
        description: data.data.description,
        department_id: parseInt(data.data.departmentId),
        manager_id: data.data.managerId ? parseInt(data.data.managerId) : null
      };
      
      console.log('Updating team with payload:', updatePayload);
      const response = await api.put(`/teams/${data.id}`, updatePayload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['departments'] }); // Teams might affect department data
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      const action = variables.data.managerId ? 'zaktualizowany' : 'zaktualizowany (kierownik usunięty)';
      toast.success(`Zespół ${action} pomyślnie`);
      setIsTeamModalOpen(false);
      setSelectedTeam(null);
      resetTeam();
    },
    onError: (error: any) => {
      console.error('Team update error:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji zespołu');
    }
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/teams/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Zespół usunięty');
      setDeleteModalOpen(false);
      setSelectedTeam(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania zespołu');
    }
  });

  const validateManager = (managerId: string, context: 'department' | 'team') => {
    if (!managerId) return true; // Manager is optional
    
    const manager = users?.find(user => user.id.toString() === managerId);
    if (!manager) {
      return 'Wybrany kierownik nie istnieje';
    }
    
    if (!manager.is_active) {
      return 'Wybrany kierownik jest nieaktywny';
    }
    
    // Check if manager has appropriate role for managing departments/teams
    const managerRoles = manager.roles || [];
    const hasManagerRole = managerRoles.some((role: any) => 
      ['admin', 'hr', 'manager'].includes(role.name?.toLowerCase())
    );
    
    if (!hasManagerRole) {
      return `Wybrany użytkownik nie ma uprawnień do kierowania ${context === 'department' ? 'działem' : 'zespołem'}`;
    }
    
    return true;
  };

  const onDeptSubmit = (data: DepartmentFormData) => {
    // Validate manager if selected
    if (data.managerId) {
      const managerValidation = validateManager(data.managerId, 'department');
      if (typeof managerValidation === 'string') {
        toast.error(managerValidation);
        return;
      }
    }
    
    if (selectedDepartment) {
      updateDepartmentMutation.mutate({ id: selectedDepartment.id, data });
    } else {
      createDepartmentMutation.mutate(data);
    }
  };

  const onTeamSubmit = (data: TeamFormData) => {
    // Validate manager if selected
    if (data.managerId) {
      const managerValidation = validateManager(data.managerId, 'team');
      if (typeof managerValidation === 'string') {
        toast.error(managerValidation);
        return;
      }
    }
    
    // Validate that team's manager is not already managing the parent department
    if (data.managerId && data.departmentId) {
      const department = departments?.find(dept => dept.id.toString() === data.departmentId);
      if (department?.managerId && department.managerId.toString() === data.managerId) {
        toast.error('Kierownik zespołu nie może być tym samym kierownikiem działu nadrzędnego');
        return;
      }
    }
    
    if (selectedTeam) {
      updateTeamMutation.mutate({ id: selectedTeam.id, data });
    } else {
      createTeamMutation.mutate(data);
    }
  };

  const openDeptModal = (department?: Department) => {
    if (department) {
      setSelectedDepartment(department);
      setDeptValue('name', department.name);
      setDeptValue('description', department.description || '');
      // Handle manager ID properly - ensure it's a string or empty string
      const managerId = department.managerId || department.manager?.id;
      setDeptValue('managerId', managerId ? managerId.toString() : '');
      console.log('Opening department modal for edit:', {
        department: department.name,
        managerId,
        managerObject: department.manager
      });
    } else {
      setSelectedDepartment(null);
      resetDept();
    }
    setIsDeptModalOpen(true);
  };

  const openTeamModal = (team?: Team) => {
    if (team) {
      setSelectedTeam(team);
      setTeamValue('name', team.name);
      setTeamValue('description', team.description || '');
      setTeamValue('departmentId', team.departmentId?.toString() || '');
      // Handle manager ID properly - ensure it's a string or empty string
      const managerId = team.managerId || team.manager?.id;
      setTeamValue('managerId', managerId ? managerId.toString() : '');
      console.log('Opening team modal for edit:', {
        team: team.name,
        departmentId: team.departmentId,
        managerId,
        managerObject: team.manager
      });
    } else {
      setSelectedTeam(null);
      resetTeam();
    }
    setIsTeamModalOpen(true);
  };

  const getTeamsByDepartment = (department: Department) => {
    // Jeśli department ma już zagregowane teams, używamy ich
    if (department.teams) {
      return department.teams;
    }
    // Fallback - filtrujemy z listy wszystkich teams
    return teams?.filter((team: Team) => team.departmentId === department.id) || [];
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">{t('departmentPage.title')}</h2>
          <div className="flex gap-2">
            <Button
              onClick={() => openTeamModal()}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('departmentPage.addTeam')}
            </Button>
            <Button
              onClick={() => openDeptModal()}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('departmentPage.addDepartment')}
            </Button>
          </div>
        </div>

        {deptLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">{t('common.loading')}</span>
          </div>
        ) : deptError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 mb-4">
              <Building className="h-12 w-12 mx-auto mb-2" />
              <h3 className="text-lg font-medium">Błąd ładowania działów</h3>
              <p className="text-sm text-red-500 mt-1">
                Nie udało się załadować listy działów. Sprawdź połączenie internetowe i spróbuj ponownie.
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <Button 
                onClick={() => refetchDepartments()}
                variant="outline"
                className="flex items-center gap-2"
              >
                {t('common.retry', 'Spróbuj ponownie')}
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                variant="secondary"
              >
                Odśwież stronę
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {(teamsError || usersError) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Ostrzeżenie dotyczące danych pomocniczych
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        {teamsError && "Nie udało się załadować zespołów. "}
                        {usersError && "Nie udało się załadować użytkowników do wyboru kierowników. "}
                        Niektóre funkcje mogą być ograniczone.
                      </p>
                      <div className="mt-2 flex gap-2">
                        {teamsError && (
                          <button
                            onClick={() => refetchTeams()}
                            className="text-yellow-800 underline hover:text-yellow-900"
                          >
                            Odśwież zespoły
                          </button>
                        )}
                        {usersError && (
                          <button
                            onClick={() => refetchUsers()}
                            className="text-yellow-800 underline hover:text-yellow-900"
                          >
                            Odśwież użytkowników
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {departments?.map((department: Department) => (
              <div
                key={department.id}
                className="bg-white border border-gray-200 rounded-lg p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <Building className="h-5 w-5 text-gray-500" />
                      <h3 className="text-lg font-medium">{department.name}</h3>
                    </div>
                    {department.description && (
                      <p className="text-gray-600 mt-1">{department.description}</p>
                    )}
                    {department.manager && (
                      <p className="text-sm text-gray-500 mt-2">
                        {t('departmentPage.manager')}: {department.manager.first_name} {department.manager.last_name}
                      </p>
                    )}
                    {department.supervisors && department.supervisors.length > 0 && (
                      <div className="text-sm text-gray-500 mt-2">
                        <span>{t('departmentPage.supervisors', 'Nadzorcy')}: </span>
                        {department.supervisors.map((supervisor: any, index: number) => (
                          <span key={supervisor.id}>
                            {supervisor.first_name} {supervisor.last_name}
                            {index < department.supervisors.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeptModal(department)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedDepartment(department);
                        setDeleteType('department');
                        setDeleteModalOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Zespoły</h4>
                  {getTeamsByDepartment(department).length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getTeamsByDepartment(department).map((team: Team) => (
                        <div
                          key={team.id}
                          className="bg-gray-50 border border-gray-200 rounded-md p-3 flex justify-between items-center"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-gray-500" />
                              <span className="font-medium">{team.name}</span>
                            </div>
                            {team.manager && (
                              <p className="text-xs text-gray-500 mt-1">
                                Kierownik: {team.manager.first_name} {team.manager.last_name}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => openTeamModal(team)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => {
                                setSelectedTeam(team);
                                setDeleteType('team');
                                setDeleteModalOpen(true);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Brak zespołów w tym dziale</p>
                  )}
                </div>
              </div>
            ))}
            {(!departments || departments.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                Brak działów. Utwórz pierwszy, aby rozpocząć.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Department Modal */}
      <Modal
        isOpen={isDeptModalOpen}
        onClose={() => {
          setIsDeptModalOpen(false);
          setSelectedDepartment(null);
          resetDept();
        }}
        title={selectedDepartment ? 'Edytuj dział' : 'Dodaj dział'}
      >
        <form onSubmit={handleDeptSubmit(onDeptSubmit)} className="space-y-4">
          <Input
            label="Nazwa"
            {...registerDept('name', { required: 'Nazwa jest wymagana' })}
            error={deptErrors.name?.message}
          />

          <Textarea
            label="Opis"
            {...registerDept('description')}
            rows={3}
          />

          <Select
            label="Kierownik"
            {...registerDept('managerId')}
            disabled={usersError}
          >
            <option value="">
              {usersError ? 'Błąd ładowania użytkowników' : 'Wybierz kierownika (opcjonalne)'}
            </option>
            {users?.filter((user: any) => {
              // Show only active users with manager roles
              if (!user.is_active) return false;
              const userRoles = user.roles || [];
              return userRoles.some((role: any) => 
                ['admin', 'hr', 'manager'].includes(role.name?.toLowerCase())
              );
            }).map((user: any) => (
              <option key={user.id} value={user.id.toString()}>
                {user.first_name} {user.last_name} 
                {user.roles?.length > 0 && ` (${user.roles.map((r: any) => r.name).join(', ')})`}
              </option>
            ))}
          </Select>
          <div className="text-xs text-gray-500">
            Tylko aktywni użytkownicy z rolami: Administrator, HR, Kierownik
          </div>
          {usersError && (
            <div className="text-sm text-red-600 flex items-center gap-2">
              <span>Nie udało się załadować użytkowników.</span>
              <button
                type="button"
                onClick={() => refetchUsers()}
                className="underline hover:no-underline"
              >
                Spróbuj ponownie
              </button>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button 
              type="submit" 
              disabled={createDepartmentMutation.isPending || updateDepartmentMutation.isPending}
            >
              {selectedDepartment ? 'Aktualizuj' : 'Utwórz'} dział
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsDeptModalOpen(false);
                setSelectedDepartment(null);
                resetDept();
              }}
            >
              Anuluj
            </Button>
          </div>
        </form>
      </Modal>

      {/* Team Modal */}
      <Modal
        isOpen={isTeamModalOpen}
        onClose={() => {
          setIsTeamModalOpen(false);
          setSelectedTeam(null);
          resetTeam();
        }}
        title={selectedTeam ? 'Edytuj zespół' : 'Dodaj zespół'}
      >
        <form onSubmit={handleTeamSubmit(onTeamSubmit)} className="space-y-4">
          <Input
            label="Nazwa"
            {...registerTeam('name', { required: 'Nazwa jest wymagana' })}
            error={teamErrors.name?.message}
          />

          <Textarea
            label="Opis"
            {...registerTeam('description')}
            rows={3}
          />

          <Select
            label="Dział"
            {...registerTeam('departmentId', { required: 'Dział jest wymagany' })}
            error={teamErrors.departmentId?.message}
            disabled={deptError}
          >
            <option value="">
              {deptError ? 'Błąd ładowania działów' : 'Wybierz dział'}
            </option>
            {departments?.map((dept: Department) => (
              <option key={dept.id} value={dept.id.toString()}>
                {dept.name}
              </option>
            ))}
          </Select>
          {deptError && (
            <div className="text-sm text-red-600 flex items-center gap-2">
              <span>Nie udało się załadować działów.</span>
              <button
                type="button"
                onClick={() => refetchDepartments()}
                className="underline hover:no-underline"
              >
                Spróbuj ponownie
              </button>
            </div>
          )}

          <Select
            label="Kierownik"
            {...registerTeam('managerId')}
            disabled={usersError}
          >
            <option value="">
              {usersError ? 'Błąd ładowania użytkowników' : 'Wybierz kierownika (opcjonalne)'}
            </option>
            {users?.filter((user: any) => {
              // Show only active users with manager roles
              if (!user.is_active) return false;
              const userRoles = user.roles || [];
              const hasManagerRole = userRoles.some((role: any) => 
                ['admin', 'hr', 'manager'].includes(role.name?.toLowerCase())
              );
              
              // Don't show the department manager if it's the same user
              const selectedDepartmentId = watchTeam('departmentId') || '';
              if (selectedDepartmentId) {
                const dept = departments?.find(d => d.id.toString() === selectedDepartmentId);
                if (dept?.managerId && dept.managerId.toString() === user.id.toString()) {
                  return false; // Exclude department manager from team manager options
                }
              }
              
              return hasManagerRole;
            }).map((user: any) => (
              <option key={user.id} value={user.id.toString()}>
                {user.first_name} {user.last_name} 
                {user.roles?.length > 0 && ` (${user.roles.map((r: any) => r.name).join(', ')})`}
              </option>
            ))}
          </Select>
          <div className="text-xs text-gray-500">
            Tylko aktywni użytkownicy z rolami kierowniczymi (wykluczając kierownika działu)
          </div>
          {usersError && (
            <div className="text-sm text-red-600 flex items-center gap-2">
              <span>Nie udało się załadować użytkowników.</span>
              <button
                type="button"
                onClick={() => refetchUsers()}
                className="underline hover:no-underline"
              >
                Spróbuj ponownie
              </button>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button 
              type="submit" 
              disabled={createTeamMutation.isPending || updateTeamMutation.isPending}
            >
              {selectedTeam ? 'Aktualizuj' : 'Utwórz'} zespół
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsTeamModalOpen(false);
                setSelectedTeam(null);
                resetTeam();
              }}
            >
              Anuluj
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedDepartment(null);
          setSelectedTeam(null);
        }}
        title={`Usuń ${deleteType === 'department' ? 'dział' : 'zespół'}`}
      >
        <div className="space-y-4">
          <p>Czy na pewno chcesz usunąć ten {deleteType === 'department' ? 'dział' : 'zespół'}? Ta operacja jest nieodwracalna.</p>
          {deleteType === 'department' && selectedDepartment && getTeamsByDepartment(selectedDepartment).length > 0 && (
            <p className="text-sm text-red-600">
              Uwaga: Ten dział ma przypisane zespoły. Usunięcie go spowoduje usunięcie wszystkich powiązanych zespołów.
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={() => {
                if (deleteType === 'department' && selectedDepartment) {
                  deleteDepartmentMutation.mutate(selectedDepartment.id);
                } else if (deleteType === 'team' && selectedTeam) {
                  deleteTeamMutation.mutate(selectedTeam.id);
                }
              }}
              disabled={deleteDepartmentMutation.isPending || deleteTeamMutation.isPending}
            >
              Usuń
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedDepartment(null);
                setSelectedTeam(null);
              }}
            >
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DepartmentManagement;